import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import type { MemoryOutcomeStatus } from '../types';
import ValidationEvidencePanel from '../components/evolution/ValidationEvidencePanel';
import { C2_EVOLUTION_ENABLED } from '../evolutionFlag';

// Consulting layer (spec 11.3): queryable institutional memory. Search the logged decisions
// ("what did we decide, and did it work?"), filterable by signal / outcome / disposition, with
// full lineage on a selected decision.

const OUTCOME_TAG: Record<MemoryOutcomeStatus, string> = {
  correct: 'teal',
  incorrect: 'red',
  pending: 'muted',
};

const DISPOSITIONS = ['monitor', 'simulate', 'create_option', 'accelerate', 'escalate', 'structural_rewire'];

export default function Memory() {
  const [params, setParams] = useSearchParams();
  const [signal, setSignal] = useState('');
  const [outcome, setOutcome] = useState('');
  const [disposition, setDisposition] = useState('');
  // Deep-linkable: /memory?decision=<id> focuses one decision (e.g. from an Ask-the-system answer).
  const decisionParam = params.get('decision');
  const [selected, setSelected] = useState<string | null>(decisionParam);
  useEffect(() => {
    setSelected(decisionParam ?? null);
  }, [decisionParam]);

  function selectDecision(id: string) {
    setSelected(id);
    const next = new URLSearchParams(params);
    next.set('decision', id);
    setParams(next, { replace: true });
  }

  const filters = { signal: signal.trim() || undefined, outcome: outcome || undefined, disposition: disposition || undefined };
  const decisions = useQuery({
    queryKey: ['memory', 'decisions', filters],
    queryFn: () => api.memoryDecisions(filters),
  });
  const detail = useQuery({
    queryKey: ['memory', 'decision', selected],
    queryFn: () => api.memoryDecision(selected as string),
    enabled: selected !== null,
  });

  return (
    <div>
      <div className="ph">Review & Learning · Institutional Memory</div>
      <div className="h2">What did we decide, and did it work?</div>
      <p className="lead">Every logged decision, with its outcome graded once the actual landed.</p>

      <div className="panel" style={{ marginBottom: 14 }}>
        <div className="qhead"><span className="qdot" style={{ background: 'var(--amber)' }} />Filter</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <Field label="Signal">
            <input className="c2-input" value={signal} onChange={(e) => setSignal(e.target.value)} placeholder="e.g. S-01" />
          </Field>
          <Field label="Outcome">
            <select className="c2-input" value={outcome} onChange={(e) => setOutcome(e.target.value)}>
              <option value="">any</option>
              <option value="correct">correct</option>
              <option value="incorrect">incorrect</option>
              <option value="pending">pending</option>
            </select>
          </Field>
          <Field label="Disposition">
            <select className="c2-input" value={disposition} onChange={(e) => setDisposition(e.target.value)}>
              <option value="">any</option>
              {DISPOSITIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>
        </div>
      </div>

      <div className="panel">
        <div className="qhead">
          <span className="qdot" style={{ background: 'var(--teal)' }} />
          {decisions.data ? `${decisions.data.count} decision${decisions.data.count === 1 ? '' : 's'}` : 'Decisions'}
        </div>
        {decisions.isLoading ? (
          <div className="small faint">Loading…</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Signal</th>
                <th className="center">Engine</th>
                <th>Disposition</th>
                <th>Decision</th>
                <th>Owner</th>
                <th className="center">Did it work?</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {decisions.data?.decisions.map((d) => (
                <tr key={d.id} onClick={() => selectDecision(d.id)} style={{ cursor: 'pointer', background: selected === d.id ? 'var(--row-hover)' : undefined }}>
                  <td>{d.signal_id}{d.signal_name ? <span className="small faint"> · {d.signal_name}</span> : null}</td>
                  <td className="center small">{d.engine ?? '—'}</td>
                  <td className="small">{d.disposition}</td>
                  <td className="small">{d.decision ?? '—'}{d.reason ? <span className="faint"> · {d.reason}</span> : null}</td>
                  <td className="small">{d.owner}</td>
                  <td className="center"><span className={`tag ${OUTCOME_TAG[d.outcome_status]}`}>{d.outcome_status}</span></td>
                  <td className="small faint">{new Date(d.ts).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <div className="panel" style={{ marginTop: 14 }}>
          <div className="qhead"><span className="qdot" style={{ background: 'var(--blue)' }} />Decision lineage</div>
          {detail.isLoading || !detail.data ? (
            <div className="small faint">Loading decision…</div>
          ) : (
            <DetailView detail={detail.data} />
          )}
        </div>
      )}
      {/* Validation evidence embedded with the decision/outcome review (evolution suite; flag-gated). */}
      {C2_EVOLUTION_ENABLED && (
        <div style={{ marginTop: 14 }}>
          <ValidationEvidencePanel />
        </div>
      )}
    </div>
  );
}

function DetailView({ detail }: { detail: import('../types').MemoryDecisionDetail }) {
  const lineage = detail.lineage as Record<string, { id?: string; name?: string; statement?: string; stage?: string } | undefined>;
  const prediction = detail.prediction as { kind?: string; within_band?: boolean | null; error?: number | null; metric?: string } | null;
  return (
    <div className="grid2" style={{ gap: 16 }}>
      <div>
        <div className="field-label">Spine chain</div>
        <div className="small" style={{ lineHeight: 1.7 }}>
          <div>Signal: <b>{lineage.signal?.id ?? detail.signal_id}</b> {lineage.signal?.name}</div>
          {lineage.assumption && <div>Assumption: <b>{lineage.assumption.id}</b> — {lineage.assumption.statement}</div>}
          {lineage.objective && <div>Objective: <b>{lineage.objective.id}</b> — {lineage.objective.name}</div>}
          {lineage.money_node && <div>Money node: <b>{lineage.money_node.id}</b> — {lineage.money_node.stage}</div>}
        </div>
      </div>
      <div>
        <div className="field-label">Outcome</div>
        <div className="small" style={{ lineHeight: 1.7 }}>
          <div>Status: <span className={`tag ${OUTCOME_TAG[detail.outcome_status]}`}>{detail.outcome_status}</span></div>
          {prediction && <div>Prediction: {prediction.kind} · within band: {String(prediction.within_band)} · error {prediction.error ?? '—'}</div>}
          <div>Decider: {detail.decider ?? '—'}</div>
          <div className="faint" style={{ marginTop: 6 }}>{detail.notes}</div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="small faint" style={{ display: 'block', marginBottom: 6, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</label>
      {children}
    </div>
  );
}
